import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { toastSuccess, toastError } from '../ui/toast';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { HomeScreenSettings } from './PWAInstallGuide';
import SupportModal from './SupportModal';
import { colors as theme } from '../theme';
import { dayTitle } from '../utils/dayTitle';
import { getMyHealthSharing, recordHealthSharing } from '../services/healthSharingService';

const Toggle = ({ checked, onChange, disabled, label }) => (
  <div
    role="switch"
    aria-checked={!!checked}
    aria-label={label}
    aria-disabled={!!disabled}
    tabIndex={disabled ? -1 : 0}
    onClick={() => !disabled && onChange(!checked)}
    onKeyDown={(e) => { if (!disabled && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); onChange(!checked); } }}
    style={{
      width: '48px', height: '26px', borderRadius: '14px',
      backgroundColor: checked ? theme.gold : theme.surface3,
      position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background-color 0.2s', flexShrink: 0,
    }}
  >
    <div style={{
      width: '20px', height: '20px', borderRadius: '50%',
      backgroundColor: theme.text, position: 'absolute', top: '3px',
      left: checked ? '25px' : '3px', transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
    }} />
  </div>
);

const Section = ({ title, children }) => (
  <div style={{
    backgroundColor: theme.surface1, borderRadius: '14px',
    padding: '20px', marginBottom: '16px', border: `1px solid ${theme.surface3}`,
  }}>
    <h3 style={{ color: theme.text, margin: '0 0 16px', fontSize: '15px', fontWeight: 700 }}>
      {title}
    </h3>
    {children}
  </div>
);

// Reminder times 6am–10pm in 30-min steps
const REMINDER_TIMES = [];
for (let h = 6; h <= 22; h++) {
  REMINDER_TIMES.push(`${String(h).padStart(2, '0')}:00`);
  REMINDER_TIMES.push(`${String(h).padStart(2, '0')}:30`);
}

const formatTime = (t) => {
  if (!t) return '5:00 PM';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
};

// ── CSV helpers ────────────────────────────────────────────────────────────

const escapeCSV = (v) => {
  if (v == null) return '';
  const s = String(v);
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCSV = (headers, rows) => {
  const lines = [headers.join(',')];
  rows.forEach(row => lines.push(row.map(escapeCSV).join(',')));
  return lines.join('\n');
};

const downloadFile = (filename, content, mimeType = 'text/csv') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Sharing health information with the coach (migration 083): on or off at any time; each change is
// recorded. Off: the coach no longer sees health answers or body scans (training and weight as before).
const HealthSharingSetting = ({ userId, Toggle }) => {
  const [shares, setShares] = useState(null); // null: not known yet
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // A failed read says so and can be tried again: an unknown answer is never shown as "off" (Codex S3)
  useEffect(() => {
    if (!userId) return undefined;
    let gone = false;
    setLoadFailed(false);
    getMyHealthSharing(userId)
      .then((s) => { if (!gone) setShares(s.answered && s.current && s.shares); })
      .catch(() => { if (!gone) setLoadFailed(true); });
    return () => { gone = true; };
  }, [userId, attempt]);
  const change = async () => {
    const next = !shares;
    setSaving(true);
    try {
      await recordHealthSharing(userId, next, 'settings');
      setShares(next);
      toastSuccess(next ? 'Your coach can now see your health information' : 'Your health information is now private');
    } catch (e) {
      toastError('Not saved. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${theme.surface3}` }}>
      <div style={{ flex: 1, marginRight: '16px' }}>
        <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500' }}>Share my health information with my coach and Cipher's lead coach</div>
        <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '2px', lineHeight: 1.5 }}>
          Health conditions, GLP-1 medication details and body scans. Eating-disorder history is never shared.
          When off, they still see your training and weight.
        </div>
        {loadFailed && (
          <div role="alert" style={{ color: theme.text, fontSize: '12px', marginTop: '6px' }}>
            Couldn't load your answer.{' '}
            <button type="button" onClick={() => setAttempt((n) => n + 1)}
              style={{ background: 'none', border: 'none', padding: 0, color: theme.gold, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
              Try again
            </button>
          </div>
        )}
      </div>
      <Toggle checked={shares === true} onChange={change} disabled={shares === null || saving} label="Share my health information with my coach and Cipher's lead coach" />
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────

const PrivacySettings = ({ onBack, userId, onLogout, guideRole = null }) => {
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [notificationTime, setNotificationTime] = useState('17:00');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingProgram, setDownloadingProgram] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState(null); // null | 'pending' | 'approved' | 'dismissed'
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [userDbId, setUserDbId] = useState(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const { isSupported, permission, isSubscribed, subscribe, unsubscribe } = usePushNotifications();

  useEffect(() => { loadSettings(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('id, name, email, notification_time, notifications_enabled')
        .eq('auth_user_id', userId)
        .single();

      if (userData) {
        setUserDbId(userData.id);
        setUserName(userData.name || '');
        setUserEmail(userData.email || '');
        setNotificationTime(userData.notification_time?.slice(0, 5) || '17:00');
        setNotificationsEnabled(userData.notifications_enabled ?? true);
      }

      // Check existing deletion request
      const { data: delReq } = await supabase
        .from('deletion_requests')
        .select('status')
        .eq('user_id', userId)
        .order('requested_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (delReq) setDeletionStatus(delReq.status);
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateNotificationTime = async (time) => {
    setNotificationTime(time);
    if (!userDbId) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('users')
        .update({ notification_time: time }).eq('id', userDbId);
      if (error) throw error;
      toastSuccess('Reminder time saved');
    } catch { toastError('Could not save'); }
    finally { setSaving(false); }
  };

  const toggleNotifications = async (enabled) => {
    setNotificationsEnabled(enabled);
    if (enabled && !isSubscribed) {
      const success = await subscribe();
      if (!success) {
        setNotificationsEnabled(false);
        toastError('Could not enable — check your browser settings');
        return;
      }
    }
    if (!enabled && isSubscribed) await unsubscribe();
    if (!userDbId) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('users')
        .update({ notifications_enabled: enabled }).eq('id', userDbId);
      if (error) throw error;
      toastSuccess(enabled ? 'Notifications on' : 'Notifications off');
    } catch { toastError('Could not save'); }
    finally { setSaving(false); }
  };

  // ── Download My Data ─────────────────────────────────────────────────────

  const handleDownloadData = async () => {
    setDownloading(true);
    try {
      // 1. Workout sessions
      const { data: sessions, error: sessErr } = await supabase
        .from('workout_sessions')
        .select('id, started_at, completed_at, rpe, notes, program_id, day_id')
        .eq('user_id', userId)
        .order('started_at', { ascending: false });

      if (sessErr) throw sessErr;

      const sessionIds = (sessions || []).map(s => s.id);
      let logs = [];
      if (sessionIds.length > 0) {
        const { data: logsData } = await supabase
          .from('workout_logs')
          .select('session_id, set_number, weight, reps, rir, completed, exercise_name')
          .in('session_id', sessionIds)
          .eq('completed', true)
          .order('session_id')
          .limit(10000);
        logs = logsData || [];
      }

      // Resolve program names
      const programIds = [...new Set((sessions || []).map(s => s.program_id).filter(Boolean))];
      const programMap = {};
      if (programIds.length > 0) {
        const { data: programs } = await supabase
          .from('programs').select('id, name').in('id', programIds);
        (programs || []).forEach(p => { programMap[p.id] = p.name; });
      }

      // Resolve day names
      const dayIds = [...new Set((sessions || []).map(s => s.day_id).filter(Boolean))];
      const dayMap = {};
      if (dayIds.length > 0) {
        const { data: days } = await supabase
          .from('program_days').select('id, name, day_name').in('id', dayIds);
        (days || []).forEach(d => { dayMap[d.id] = dayTitle(d); });
      }

      // Build workouts CSV
      const sessionMap = new Map((sessions || []).map(s => [s.id, s]));
      const workoutRows = logs.map(log => {
        const s = sessionMap.get(log.session_id);
        return [
          s?.started_at ? new Date(s.started_at).toLocaleDateString('en-GB') : '',
          s?.program_id ? (programMap[s.program_id] || '') : '',
          s?.day_id ? (dayMap[s.day_id] || '') : '',
          log.exercise_name || '',
          log.set_number,
          log.weight ?? '',
          log.reps ?? '',
          log.rir ?? '',
          s?.rpe ?? '',
          s?.notes || '',
        ];
      });
      const workoutsCSV = toCSV(
        ['Date', 'Program', 'Day', 'Exercise', 'Set', 'Weight (kg)', 'Reps', 'RIR', 'RPE', 'Session Notes'],
        workoutRows
      );

      // 2. Check-ins CSV
      const { data: checkins } = await supabase
        .from('monthly_check_ins')
        .select('created_at, bodyweight_kg, message')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      const checkinRows = (checkins || []).map(c => [
        new Date(c.created_at).toLocaleDateString('en-GB'),
        c.bodyweight_kg ?? '',
        c.message || '',
      ]);
      const checkinsCSV = toCSV(
        ['Date', 'Bodyweight (kg)', 'Notes'],
        checkinRows
      );

      // Download both files
      const date = new Date().toISOString().split('T')[0];
      downloadFile(`cipher-workouts-${date}.csv`, workoutsCSV);
      setTimeout(() => downloadFile(`cipher-checkins-${date}.csv`, checkinsCSV), 500);

      toastSuccess('Your data is downloading');
    } catch (err) {
      console.error('Download error:', err);
      toastError('Could not download data');
    } finally {
      setDownloading(false);
    }
  };

  // ── Download My Program ───────────────────────────────────────────────────

  // The week the client is on, as a PDF with a picture of each exercise. The loader and the PDF
  // library are fetched only when the button is pressed.
  const handleDownloadProgram = async () => {
    setDownloadingProgram(true);
    try {
      const [{ loadCurrentWeekForPdf }, { clientWeekPdfInput }, { generateProgramPDF }] = await Promise.all([
        import('../services/clientWeekPdfService'),
        import('../utils/clientProgramPdf'),
        import('../generateProgramPDF'),
      ]);
      const loaded = await loadCurrentWeekForPdf(userId);
      const input = loaded && clientWeekPdfInput(loaded.program, loaded.week, loaded.overrides);
      if (!input || input.weeks[0].days.length === 0) {
        toastError('There is no program to download yet.');
        return;
      }
      await generateProgramPDF(input.program, input.weeks, { createdAt: new Date(), preparedFor: userName || undefined });
      toastSuccess('Your program is downloading');
    } catch (err) {
      console.error('Program download error:', err);
      toastError('Could not download your program');
    } finally {
      setDownloadingProgram(false);
    }
  };

  // ── Request Deletion ──────────────────────────────────────────────────────

  const handleRequestDeletion = async () => {
    if (!window.confirm('Are you sure you want to request account deletion? Your coach will be notified and will action your request.')) return;
    setRequestingDeletion(true);
    try {
      const { error } = await supabase.from('deletion_requests').insert({
        user_id: userId,
        user_name: userName,
        user_email: userEmail,
        status: 'pending',
      });
      if (error) throw error;
      setDeletionStatus('pending');
      toastSuccess('Deletion request submitted. Your coach will be in touch.');
    } catch (err) {
      console.error('Deletion request error:', err);
      toastError('Could not submit request');
    } finally {
      setRequestingDeletion(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { supabase } = await import('../supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toastError(data.error || 'Could not open billing portal');
      }
    } catch {
      toastError('Could not open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px', backgroundColor: theme.bg, minHeight: '100vh', maxWidth: '600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent', border: `1px solid ${theme.surface3}`,
            color: theme.text, padding: '10px 18px', borderRadius: '8px',
            cursor: 'pointer', fontSize: '13px', fontWeight: 600,
          }}
        >
          ← Back
        </button>
        <div>
          <h1 style={{ color: theme.text, margin: 0, fontSize: '24px' }}>Settings</h1>
          <p style={{ color: theme.textMuted, margin: '4px 0 0', fontSize: '13px' }}>
            Manage your notifications and account
          </p>
        </div>
      </div>

      {loading ? (
        <p style={{ color: theme.textMuted }}>Loading settings...</p>
      ) : (
        <>
          <HomeScreenSettings role={guideRole} />
          {/* Notifications */}
          <Section title="Notifications">
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0', borderBottom: notificationsEnabled && isSubscribed ? `1px solid ${theme.surface3}` : 'none',
            }}>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500' }}>
                  Workout reminders
                </div>
                <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '2px' }}>
                  {!isSupported
                    ? 'Not supported on this device'
                    : permission === 'denied'
                    ? 'Blocked — enable in browser settings'
                    : 'Daily nudge when your next session is ready'}
                </div>
              </div>
              <Toggle
                label="Workout reminders"
                checked={notificationsEnabled && isSubscribed}
                onChange={toggleNotifications}
                disabled={!isSupported || permission === 'denied'}
              />
            </div>

            {notificationsEnabled && isSubscribed && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 0',
              }}>
                <div>
                  <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500' }}>
                    Reminder time
                  </div>
                  <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '2px' }}>
                    Notified at this time on training days
                  </div>
                </div>
                <select
                  value={notificationTime}
                  onChange={(e) => updateNotificationTime(e.target.value)}
                  style={{
                    backgroundColor: theme.surface3, border: `1px solid ${theme.surface3}`,
                    borderRadius: '8px', color: theme.text, padding: '8px 12px',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer', outline: 'none',
                  }}
                >
                  {REMINDER_TIMES.map(t => (
                    <option key={t} value={t}>{formatTime(t)}</option>
                  ))}
                </select>
              </div>
            )}
          </Section>

          {/* Your Data */}
          <Section title="Your Data">
            <HealthSharingSetting userId={userId} Toggle={Toggle} />
            {/* Download */}
            <div style={{ padding: '12px 0', borderBottom: `1px solid ${theme.surface3}` }}>
              <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                Download my data
              </div>
              <div style={{ color: theme.textMuted, fontSize: '12px', marginBottom: '12px' }}>
                Export your full workout history and check-ins as CSV files you can open in Excel or Google Sheets.
              </div>
              <button
                onClick={handleDownloadData}
                disabled={downloading}
                style={{
                  padding: '10px 18px', borderRadius: '8px',
                  border: `1px solid ${theme.surface3}`, backgroundColor: 'transparent',
                  color: theme.text, cursor: downloading ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: 600, opacity: downloading ? 0.6 : 1,
                }}
              >
                {downloading ? 'Preparing…' : '⬇ Download My Data'}
              </button>
            </div>

            {/* Download the program */}
            <div style={{ padding: '12px 0', borderBottom: `1px solid ${theme.surface3}` }}>
              <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                Download my program
              </div>
              <div style={{ color: theme.textMuted, fontSize: '12px', marginBottom: '12px' }}>
                The week of your program you are on now, as a PDF with a picture of each exercise. Yours to keep.
              </div>
              <button
                onClick={handleDownloadProgram}
                disabled={downloadingProgram}
                style={{
                  padding: '10px 18px', borderRadius: '8px',
                  border: `1px solid ${theme.surface3}`, backgroundColor: 'transparent',
                  color: theme.text, cursor: downloadingProgram ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: 600, opacity: downloadingProgram ? 0.6 : 1,
                }}
              >
                {downloadingProgram ? 'Preparing…' : '⬇ Download My Program (PDF)'}
              </button>
            </div>

            {/* Log out */}
            {onLogout && (
              <div style={{ padding: '12px 0', borderBottom: `1px solid ${theme.surface3}` }}>
                <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                  Log out
                </div>
                <div style={{ color: theme.textMuted, fontSize: '12px', marginBottom: '12px' }}>
                  You'll need to sign in again to see your workouts.
                </div>
                {confirmingLogout ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setConfirmingLogout(false)}
                      style={{
                        padding: '10px 18px', borderRadius: '8px',
                        border: `1px solid ${theme.borderStrong}`, backgroundColor: 'transparent',
                        color: theme.text, cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { setConfirmingLogout(false); onLogout(); }}
                      style={{
                        padding: '10px 18px', borderRadius: '8px',
                        border: `1px solid ${theme.danger}`, backgroundColor: theme.danger,
                        color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                      }}
                    >
                      Yes, log out
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingLogout(true)}
                    style={{
                      padding: '10px 18px', borderRadius: '8px',
                      border: `1px solid ${theme.borderStrong}`, backgroundColor: 'transparent',
                      color: theme.text, cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    }}
                  >
                    Log out
                  </button>
                )}
              </div>
            )}

            {/* Deletion */}
            <div style={{ padding: '12px 0' }}>
              <div style={{ color: theme.text, fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                Request account deletion
              </div>
              <div style={{ color: theme.textMuted, fontSize: '12px', marginBottom: '12px' }}>
                Your coach will be notified and will action your request in accordance with GDPR.
              </div>

              {deletionStatus === 'pending' ? (
                <div style={{
                  padding: '12px 16px', borderRadius: '8px',
                  backgroundColor: 'rgba(255,149,0,0.1)', border: '1px solid rgba(255,149,0,0.3)',
                  color: theme.warning, fontSize: '13px',
                }}>
                  ⏳ Deletion request submitted — your coach will be in touch.
                </div>
              ) : deletionStatus === 'dismissed' ? (
                <div style={{
                  padding: '12px 16px', borderRadius: '8px',
                  backgroundColor: 'rgba(142,142,147,0.1)', border: '1px solid rgba(142,142,147,0.3)',
                  color: theme.textMuted, fontSize: '13px',
                }}>
                  Your previous request was reviewed. Contact your coach directly if you still wish to delete your account.
                </div>
              ) : (
                <button
                  onClick={handleRequestDeletion}
                  disabled={requestingDeletion}
                  style={{
                    padding: '10px 18px', borderRadius: '8px',
                    border: `1px solid ${theme.danger}`, backgroundColor: 'transparent',
                    color: theme.danger, cursor: requestingDeletion ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: 600, opacity: requestingDeletion ? 0.6 : 1,
                  }}
                >
                  {requestingDeletion ? 'Submitting…' : 'Request Account Deletion'}
                </button>
              )}
            </div>
          </Section>

          {/* Support */}
          <Section title="Help & Support">
            <p style={{ color: theme.textMuted, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
              Having an issue? We'll reply to your account email within 24 hours.
            </p>
            <button
              onClick={() => setSupportOpen(true)}
              style={{
                width: '100%', padding: '14px', borderRadius: '8px',
                background: theme.surface3, color: theme.text,
                border: `1px solid ${theme.surface3}`, fontSize: '14px', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Contact Support
            </button>
          </Section>

          {/* Subscription */}
          <Section title="Subscription">
            <p style={{ color: theme.textMuted, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
              Manage your billing, update your payment method, or cancel your subscription.
            </p>
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              style={{
                width: '100%', padding: '14px', borderRadius: '8px',
                background: portalLoading ? theme.surface3 : theme.gold,
                color: portalLoading ? theme.textMuted : theme.bg,
                border: 'none', fontSize: '14px', fontWeight: 700,
                cursor: portalLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          </Section>

          {saving && (
            <p style={{ color: theme.textMuted, textAlign: 'center', fontSize: '13px' }}>Saving...</p>
          )}
        </>
      )}

      {supportOpen && (
        <SupportModal userEmail={userEmail} onClose={() => setSupportOpen(false)} />
      )}
    </div>
  );
};

export default PrivacySettings;
